import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AiAttachableDocument,
  AiConversation,
  AiConversationDetail,
  AiStoredMessage,
  AiStreamFrame,
} from '@accounting/shared';
import { api, ApiError, streamNdjson } from '../lib/api';

export const aiKeys = {
  conversations: (archived: boolean) => ['ai', 'conversations', archived] as const,
  conversation: (id: string) => ['ai', 'conversation', id] as const,
  attachable: (q: string) => ['ai', 'attachable-documents', q] as const,
};

/** Last thread the user was in, so a reload lands back where they were. */
const LAST_THREAD_KEY = 'ask-ai:last-conversation';

/**
 * A message as the chat renders it. The in-flight assistant turn is the same
 * shape as a stored one with `pending: true`, so the transcript never has to
 * special-case "the one that is still arriving".
 */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolsUsed: string[];
  attachedDocumentIds: string[];
  pending?: boolean;
  stopped?: boolean;
}

function toChatMessage(m: AiStoredMessage): ChatMessage {
  return {
    id: m.id,
    role: m.role,
    content: m.content,
    toolsUsed: m.toolsUsed,
    attachedDocumentIds: m.attachedDocumentIds,
  };
}

export function useAiConversations(archived: boolean) {
  return useQuery({
    queryKey: aiKeys.conversations(archived),
    queryFn: () =>
      api.get<AiConversation[]>('/ai/conversations', archived ? { archived: 'true' } : undefined),
  });
}

/** Vault documents offered in the attach picker. */
export function useAttachableDocuments(q: string, enabled: boolean) {
  return useQuery({
    queryKey: aiKeys.attachable(q),
    enabled,
    queryFn: () =>
      api.get<AiAttachableDocument[]>('/ai/attachable-documents', q ? { q } : undefined),
  });
}

/**
 * The whole Ask AI surface: thread selection, transcript, streaming, stop and
 * regenerate.
 *
 * Streaming is the reason this is a hook rather than TanStack Query state — the
 * transcript mutates dozens of times a second while a reply is arriving, and
 * only settles into the query cache once the turn is done.
 */
export function useAiChat() {
  const qc = useQueryClient();
  const [archived, setArchived] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : window.localStorage.getItem(LAST_THREAD_KEY),
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const conversations = useAiConversations(archived);

  const selectConversation = useCallback((id: string | null) => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
    setActiveTool(null);
    setError(null);
    setActiveId(id);
    setMessages([]);
    if (typeof window !== 'undefined') {
      if (id) window.localStorage.setItem(LAST_THREAD_KEY, id);
      else window.localStorage.removeItem(LAST_THREAD_KEY);
    }
  }, []);

  const detail = useQuery({
    queryKey: aiKeys.conversation(activeId ?? ''),
    enabled: !!activeId,
    queryFn: () => api.get<AiConversationDetail>(`/ai/conversations/${activeId}`),
  });

  // A thread that 404s (deleted in another tab) must not wedge the page.
  useEffect(() => {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      selectConversation(null);
    }
  }, [detail.error, selectConversation]);

  // Latest transcript, readable from an effect without making it a dependency.
  const messagesRef = useRef<ChatMessage[]>(messages);
  messagesRef.current = messages;

  // Load the transcript when a thread is opened. Two things must never be
  // clobbered: a reply that is streaming in, and an optimistic turn in a thread
  // the server has not yet caught up on (a brand-new thread's cache entry is
  // legitimately empty for the moment between "created" and "first message
  // saved").
  useEffect(() => {
    if (streaming) return;
    const server = detail.data?.messages ?? [];
    if (server.length === 0 && messagesRef.current.length > 0) return;
    setMessages(server.map(toChatMessage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.data]);

  const invalidateLists = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['ai', 'conversations'] });
  }, [qc]);

  const startConversation = useCallback(async (): Promise<string> => {
    const conv = await api.post<AiConversation>('/ai/conversations', {});
    qc.setQueryData(aiKeys.conversation(conv.id), {
      conversation: conv,
      messages: [],
    } satisfies AiConversationDetail);
    invalidateLists();
    selectConversation(conv.id);
    return conv.id;
  }, [qc, invalidateLists, selectConversation]);

  /**
   * Consume one streamed turn.
   *
   * The optimistic user bubble is added by the caller for `send`; frames only
   * ever append to the trailing assistant bubble, so a stop leaves the partial
   * answer on screen exactly as the server saved it.
   */
  const consume = useCallback(
    async (path: string, body: unknown) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setStreaming(true);
      setError(null);
      setActiveTool(null);

      const pendingId = `pending-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        { id: pendingId, role: 'assistant', content: '', toolsUsed: [], attachedDocumentIds: [], pending: true },
      ]);

      const patchPending = (fn: (m: ChatMessage) => ChatMessage) =>
        setMessages((prev) => prev.map((m) => (m.id === pendingId ? fn(m) : m)));

      try {
        await streamNdjson<AiStreamFrame>(
          path,
          body,
          (frame) => {
            switch (frame.type) {
              case 'delta':
                patchPending((m) => ({ ...m, content: m.content + frame.text }));
                break;
              case 'tool':
                setActiveTool(frame.name);
                patchPending((m) => ({ ...m, toolsUsed: [...m.toolsUsed, frame.name] }));
                break;
              case 'done':
                setActiveTool(null);
                patchPending((m) => ({
                  ...m,
                  id: frame.messageId,
                  toolsUsed: frame.toolsUsed,
                  pending: false,
                  stopped: frame.stopped,
                }));
                break;
              case 'title':
                invalidateLists();
                break;
              case 'error':
                setError(frame.message);
                setMessages((prev) => prev.filter((m) => m.id !== pendingId));
                break;
              default:
                break;
            }
          },
          controller.signal,
        );
      } catch (err) {
        if (controller.signal.aborted) {
          // Stop button: the server saved the partial answer, so pull the
          // authoritative transcript rather than guessing what it kept.
          patchPending((m) => ({ ...m, pending: false, stopped: true }));
        } else {
          setMessages((prev) => prev.filter((m) => m.id !== pendingId));
          setError(
            err instanceof ApiError && err.status === 503
              ? 'The AI assistant is not configured on the server yet.'
              : 'Something went wrong answering that. Please try again.',
          );
        }
      } finally {
        abortRef.current = null;
        setStreaming(false);
        setActiveTool(null);
        invalidateLists();
      }
    },
    [invalidateLists],
  );

  const send = useCallback(
    async (text: string, attachedDocumentIds: string[] = []) => {
      const content = text.trim();
      if (!content || streaming) return;
      const id = activeId ?? (await startConversation());

      setMessages((prev) => [
        ...prev,
        {
          id: `local-${Date.now()}`,
          role: 'user',
          content,
          toolsUsed: [],
          attachedDocumentIds,
        },
      ]);
      await consume(`/ai/conversations/${id}/messages`, { content, attachedDocumentIds });
      void qc.invalidateQueries({ queryKey: aiKeys.conversation(id) });
    },
    [activeId, consume, qc, startConversation, streaming],
  );

  const regenerate = useCallback(async () => {
    if (!activeId || streaming) return;
    // Drop the last answer locally; the server discards it too.
    setMessages((prev) => {
      const next = [...prev];
      while (next.length && next[next.length - 1]!.role === 'assistant') next.pop();
      return next;
    });
    await consume(`/ai/conversations/${activeId}/regenerate`, {});
    void qc.invalidateQueries({ queryKey: aiKeys.conversation(activeId) });
  }, [activeId, consume, qc, streaming]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      api.patch<AiConversation>(`/ai/conversations/${id}`, { title }),
    onSuccess: (conv) => {
      invalidateLists();
      void qc.invalidateQueries({ queryKey: aiKeys.conversation(conv.id) });
    },
  });

  const setArchivedFlag = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      api.patch<AiConversation>(`/ai/conversations/${id}`, { archived: value }),
    onSuccess: (conv) => {
      invalidateLists();
      if (conv.id === activeId) selectConversation(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/ai/conversations/${id}`),
    onSuccess: (_data, id) => {
      invalidateLists();
      if (id === activeId) selectConversation(null);
    },
  });

  const canRegenerate = useMemo(
    () => !streaming && messages.some((m) => m.role === 'assistant'),
    [messages, streaming],
  );

  return {
    // thread list
    conversations: conversations.data ?? [],
    conversationsLoading: conversations.isLoading,
    archived,
    setArchived,
    activeId,
    activeConversation: detail.data?.conversation ?? null,
    selectConversation,
    startConversation,
    rename,
    setArchivedFlag,
    remove,
    // transcript
    messages,
    loadingThread: !!activeId && detail.isLoading,
    streaming,
    activeTool,
    error,
    canRegenerate,
    send,
    stop,
    regenerate,
  };
}
